import { useAccount } from "../lib/account.ts";
import { useVerificationsController } from "../verifications/useVerificationsController.ts";
import type { CaseCard } from "../verifications/attributionService.model.ts";
import type { RequestCard } from "../verifications/verifications.model.ts";
import { AUTHENTICATIONS_COPY } from "./authentications.constants.ts";
import {
  authorshipRequests,
  caseFor,
  confirmedRows,
  serviceStrip,
  splitAwaiting,
  studioCases,
} from "./authentications.model.ts";
import type { CatalogueRow } from "./catalogue.model.ts";
import { useCatalogueController } from "./useCatalogueController.ts";

/**
 * The Authentication Requests screen's controller — OND-212.
 *
 * COMPOSED OF TWO CONTROLLERS THAT ALREADY EXIST, and that is the design. The
 * inbox controller owns the requests, the documents, the answer and the
 * service's five writes; the catalogue controller owns what this estate has
 * confirmed. This hook asks both and narrows what they answer. A third fetch
 * path for the same rows would be a third place to get "who may answer" wrong.
 *
 * The catalogue is read with `canDeclare: false`: this screen offers no
 * declaration control, so no seat is offered one here regardless of rung.
 */
export type AuthenticationsView =
  | { kind: "loading"; message: string }
  | { kind: "error"; message: string }
  | {
      kind: "open";
      awaiting: RequestCard[];
      answered: RequestCard[];
      /** The service line under each awaiting request, keyed by request. */
      strips: Record<string, string | null>;
      cases: CaseCard[];
      confirmed: CatalogueRow[] | null;
      busy: boolean;
      error: string | null;
    };

export function useAuthenticationsController(input: {
  principalOndoId: string;
  workBase: string;
}) {
  const { me: meState } = useAccount();
  const me = meState.status === "ready" ? meState.me : null;

  const inbox = useVerificationsController({
    me,
    principalOndoId: input.principalOndoId,
    focus: null,
  });
  const catalogue = useCatalogueController({ workBase: input.workBase, canDeclare: false });

  const view: AuthenticationsView = (() => {
    if (inbox.view.kind === "loading")
      return { kind: "loading", message: AUTHENTICATIONS_COPY.loading };
    if (inbox.view.kind === "error") return { kind: "error", message: inbox.view.message };

    const requests = authorshipRequests(inbox.view.rows);
    const { awaiting, answered } = splitAwaiting(requests);
    const cases = studioCases(inbox.view.cases);
    const strips = Object.fromEntries(
      awaiting.map((row) => [row.requestId, serviceStrip(caseFor(cases, row.requestId))]),
    );

    return {
      kind: "open",
      awaiting,
      answered,
      strips,
      cases,
      // The catalogue is a second read and a slower one; the queue does not
      // wait for it. Null while loading or refused, and the section says so.
      confirmed: catalogue.view.kind === "open" ? confirmedRows(catalogue.view.rows) : null,
      busy: inbox.view.busy,
      error: inbox.view.error,
    };
  })();

  return {
    view,
    capacities: inbox.view.kind === "open" ? inbox.view.capacities : [],
    selected: inbox.view.kind === "open" ? inbox.view.selected : [],
    caseLog: inbox.view.kind === "open" ? inbox.view.caseLog : {},
    images: inbox.images,
    thumbs: catalogue.thumbs,
    spotlight: inbox.spotlight,
    closeDocument: inbox.closeDocument,
    openDocument: inbox.openDocument,
    toggle: inbox.toggle,
    clearSelection: inbox.clearSelection,
    answer: inbox.answer,
    answerMany: inbox.answerMany,
    refer: inbox.refer,
    addDocument: inbox.addDocument,
    openCaseLog: inbox.openCaseLog,
    askEvidence: inbox.askEvidence,
    extendTarget: inbox.extendTarget,
    assignReviewer: inbox.assignReviewer,
    recordFeePaid: inbox.recordFeePaid,
    correctFee: inbox.correctFee,
  };
}
